import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";
import AuthContext from "../context/AuthContext";
import ScoreEntryWizard from "./ScoreEntryModal";
import { getYouTubeEmbedUrl } from "../utils/youtube";
import "./MatchDetail.css";

const API_URL = process.env.REACT_APP_API_URL;

const matchLineup = [
  { index: 1, type: 2, description: "Đôi 1" },
  { index: 2, type: 1, description: "A - X" },
  { index: 3, type: 1, description: "B - Y" },
  { index: 4, type: 2, description: "Đôi 2" },
  { index: 5, type: 1, description: "C - Z" },
  { index: 6, type: 1, description: "D - W" },
  { index: 7, type: 2, description: "Đôi 3" },
  { index: 8, type: 1, description: "A - Y" },
  { index: 9, type: 1, description: "B - X" },
];

const MatchDetail = () => {
  const { scheduleId } = useParams();
  const { user } = useContext(AuthContext);

  const [match, setMatch] = useState(null);
  const [subMatches, setSubMatches] = useState([]);
  const [homePlayers, setHomePlayers] = useState([]);
  const [guestPlayers, setGuestPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [needsRefresh, setNeedsRefresh] = useState(false);

  // Wizard State
  const [showWizard, setShowWizard] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSubMatch, setCurrentSubMatch] = useState(null);
  const [currentLineupItem, setCurrentLineupItem] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const matchRes = await axios.get(`${API_URL}/schedules/${scheduleId}`);
      setMatch(matchRes.data);

      const [subMatchesRes, homePlayersRes, guestPlayersRes] =
        await Promise.all([
          axios.get(`${API_URL}/details/match-details/${scheduleId}`),
          axios.get(
            `${API_URL}/teams/${matchRes.data.home}/players?season_id=${matchRes.data.season_id}`,
          ),
          axios.get(
            `${API_URL}/teams/${matchRes.data.guest}/players?season_id=${matchRes.data.season_id}`,
          ),
        ]);
      setSubMatches(subMatchesRes.data);
      setHomePlayers(homePlayersRes.data);
      setGuestPlayers(guestPlayersRes.data);
    } catch (err) {
      setError("Không thể tải dữ liệu trận đấu.");
    } finally {
      setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, needsRefresh]);

  const handleShowAdd = (lineupItem) => {
    setIsEditing(false);
    setCurrentLineupItem(lineupItem);
    setCurrentSubMatch(null);
    setShowWizard(true);
  };

  const handleShowEdit = (subMatch, lineupItem) => {
    setIsEditing(true);
    setCurrentLineupItem(lineupItem);
    setCurrentSubMatch(subMatch);
    setShowWizard(true);
  };

  const handleCloseWizard = () => {
    setShowWizard(false);
    setCurrentSubMatch(null);
    setCurrentLineupItem(null);
  };



  const handleSaveSubMatch = async (formData) => {
    const payload = {
      ...formData,
      schedule_id: scheduleId,
      // Convert sets array to individual fields
      home_sec_1: formData.sets[0],
      guest_sec_1: formData.sets[1],
      home_sec_2: formData.sets[2],
      guest_sec_2: formData.sets[3],
      home_sec_3: formData.sets[4],
      guest_sec_3: formData.sets[5],
      home_sec_4: formData.sets[6],
      guest_sec_4: formData.sets[7],
      home_sec_5: formData.sets[8],
      guest_sec_5: formData.sets[9],
      home_result: formData.sets[10],
      guest_result: formData.sets[11],
    };

    try {
      if (isEditing && currentSubMatch?.id) {
        await axios.put(
          `${API_URL}/details/match-details/${currentSubMatch.id}`,
          payload
        );
      } else {
        await axios.post(`${API_URL}/details/match-details`, payload);
      }
      setNeedsRefresh((p) => !p);
      handleCloseWizard();
    } catch (err) {
      setError(err.response?.data || "Lỗi khi lưu trận đấu con.");
    }
  };

  const handleDeleteSubMatch = async (id) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa trận đấu con này?")) {
      await axios.delete(`${API_URL}/details/match-details/${id}`);
      setNeedsRefresh((p) => !p);
    }
  };

  const handleManagerConfirm = async () => {
    if (window.confirm("Bạn có chắc chắn muốn xác nhận kết quả này?")) {
      try {
        await axios.post(`${API_URL}/schedules/${scheduleId}/confirm-result`);
        setNeedsRefresh((p) => !p);
      } catch (err) {
        setError(err.response?.data || "Lỗi khi xác nhận.");
      }
    }
  };

  if (loading)
    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  if (error)
    return (
      <Alert variant="danger" onClose={() => setError("")} dismissible>
        {error}
      </Alert>
    );
  if (!match) return <Alert variant="warning">Không tìm thấy trận đấu.</Alert>;

  const isAdmin = user && user.role === "admin";
  const isHomeManager =
    user && user.role === "manager" && String(user.teamId) === String(match.home);
  const isGuestManager =
    user && user.role === "manager" && String(user.teamId) === String(match.guest);
  const canEditScores = isAdmin || (isHomeManager && match.status === 0);
  const canEditMissingVideos =
    isGuestManager && match.status === 0 && !match.guest_has_confirmed;
  const canShowActions =
    (canEditScores || canEditMissingVideos) && match.status === 0;
  const missingVideoSubMatches = subMatches.filter((sm) => !sm.video);
  const showMissingVideoWarning =
    (isAdmin || isHomeManager || isGuestManager) && missingVideoSubMatches.length > 0;

  return (
    <div>
      <Card className="mb-4">
        <Card.Header>
          <Card.Title as="h2">
            {match.home_team_name} vs {match.guest_team_name}
          </Card.Title>
          <Card.Subtitle className="text-muted">
            Ngày: {match.play_date}
          </Card.Subtitle>
        </Card.Header>
        <Card.Body>
          <Row className="text-center">
            <Col>
              <h4>{match.home_team_name}</h4>
              <h1 className="display-1">{match.home_result ?? "-"}</h1>
            </Col>
            <Col>
              <h4>{match.guest_team_name}</h4>
              <h1 className="display-1">{match.guest_result ?? "-"}</h1>
            </Col>
          </Row>
          <div className="text-center mt-2">
            {(match.status === 0 && match.home_result === null) ? <Badge bg="secondary">Chưa có kết quả</Badge> : null}
            {(match.status === 0 && match.home_result !== null && !match.guest_has_confirmed) ? <Badge bg="warning">Chờ đội khách xác nhận</Badge> : null}
            {(match.status === 0 && match.guest_has_confirmed) ? <Badge bg="info">Đã xác nhận, chờ Admin tính điểm</Badge> : null}
            {(match.status === 1) ? <Badge bg="success">Đã tính điểm</Badge> : null}
          </div>
        </Card.Body>
      </Card>

      <h4 className="mt-4">Chi tiết các trận đấu con</h4>
      {showMissingVideoWarning && (
        <Alert variant="warning">
          Còn {missingVideoSubMatches.length} trận thiếu video. Đội có thể bị phạt theo điều lệ nếu không bổ sung video.
        </Alert>
      )}
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Trận</th>
              <th>VĐV Đội nhà</th>
              <th>Kết quả</th>
              <th>VĐV Đội khách</th>
              <th>Video</th>
              {canShowActions && <th>Hành động</th>}
            </tr>
          </thead>
          <tbody>
            {matchLineup.map((lineupItem) => {
              const subMatch = subMatches.find(
                (sm) => sm.math_index === lineupItem.index,
              );
              return (
                <tr key={lineupItem.index}>
                  <td>{lineupItem.index}</td>
                  <td>{lineupItem.description}</td>
                  <td>
                    {subMatch
                      ? lineupItem.type === 1
                        ? subMatch.p1_name
                        : `${subMatch.p1_name} & ${subMatch.p2_name}`
                      : ""}
                  </td>
                  <td className="fw-bold">
                    {subMatch
                      ? `${subMatch.home_result} - ${subMatch.guest_result}`
                      : "-"}
                  </td>
                  <td>
                    {subMatch
                      ? lineupItem.type === 1
                        ? subMatch.p3_name
                        : `${subMatch.p3_name} & ${subMatch.p4_name}`
                      : ""}
                  </td>
                  <td>
                    {subMatch && subMatch.video ? (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          setCurrentVideoUrl(subMatch.video);
                          setShowVideoModal(true);
                        }}
                      >
                        Xem
                      </Button>
                    ) : (
                      <Badge bg="light" text="dark">
                        Chưa có
                      </Badge>
                    )}
                  </td>
                  {canShowActions && (
                    <td>
                      {subMatch ? (
                        canEditScores && (isAdmin || subMatch.guest_status !== 1) ? (
                          <>
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => handleShowEdit(subMatch, lineupItem)}
                            >
                              Sửa
                            </Button>
                            {subMatch.guest_status !== 1 && (
                              <>
                                {" "}
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleDeleteSubMatch(subMatch.id)}
                                >
                                  Xóa
                                </Button>
                              </>
                            )}
                          </>
                        ) : canEditMissingVideos && !subMatch.video ? (
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => handleShowEdit(subMatch, lineupItem)}
                          >
                            Bổ sung video
                          </Button>
                        ) : (
                          <Badge bg={subMatch.guest_status === 1 ? "success" : "secondary"}>
                            {subMatch.guest_status === 1 ? "Đã xác nhận" : "Không có quyền"}
                          </Badge>
                        )
                      ) : canEditScores ? (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleShowAdd(lineupItem)}
                        >
                          Thêm
                        </Button>
                      ) : (
                        <Badge bg="secondary">Chưa có KQ</Badge>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="mt-4 d-flex justify-content-end">
        {(isGuestManager || isAdmin) &&
          match.status === 0 &&
          match.home_result !== null &&
          !match.guest_has_confirmed && (
            <Button variant="success" size="lg" onClick={handleManagerConfirm}>
              Xác Nhận Kết Quả
            </Button>
          )}
      </div>

      {/* Score Entry Wizard */}
      {showWizard && currentLineupItem && (
        <ScoreEntryWizard
          match={match}
          lineupItem={currentLineupItem}
          homePlayers={homePlayers}
          guestPlayers={guestPlayers}
          initialData={currentSubMatch}
          isEditing={isEditing}
          videoOnly={canEditMissingVideos && !canEditScores}
          onSave={handleSaveSubMatch}
          onCancel={handleCloseWizard}
        />
      )}

      {/* Video Modal */}
      <Modal
        show={showVideoModal}
        onHide={() => {
          setShowVideoModal(false);
          setCurrentVideoUrl("");
        }}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Video Trận Đấu</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {currentVideoUrl ? (
            (() => {
              const embedUrl = getYouTubeEmbedUrl(currentVideoUrl);
              if (embedUrl) {
                return (
                  <div className="ratio ratio-16x9">
                    <iframe
                      src={embedUrl}
                      title="Video trận đấu"
                      allowFullScreen
                    />
                  </div>
                );
              }
              return (
                <div className="text-center py-4">
                  <p className="text-muted">Link video không hỗ trợ xem trực tiếp</p>
                  <a href={currentVideoUrl} target="_blank" rel="noopener noreferrer" className="d-block mb-3">{currentVideoUrl}</a>
                  <Button variant="primary" onClick={() => window.open(currentVideoUrl, "_blank")}>Mở trong tab mới</Button>
                </div>
              );
            })()
          ) : (
            <div className="text-center py-5">
              <p>Không có video để hiển thị.</p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MatchDetail;
